/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, type PropsWithChildren } from 'react'

import { useRegenOffer } from '@/api/hooks/useRegenOffer'
import { useRegenState, type RegenState } from '@/api/hooks/useRegenState'
import { useIndividuals } from '@/hooks/useIndividuals'

export type RegenContextValue = RegenState & {
  /** Whether to surface the "new knowledge" offer (banner + sidebar dot). */
  offer: ReturnType<typeof useRegenOffer>
}

const RegenContext = createContext<RegenContextValue | undefined>(undefined)

/**
 * Holds the single regen subscription for the whole authenticated app. Mounted
 * in the `_authenticated` layout so the RTDB listener stays alive on every page,
 * not just the home page — a regen finishing while the user sits on /services
 * still invalidates the queries it rewrote (see useRegenRealtime).
 *
 * The regen *offer* is derived here for the same reason: it subscribes to every
 * in-flight document, and the sidebar and the home banner both need it. Deriving
 * it in each would open two listener sets on the same nodes and invalidate the
 * individual query twice per push.
 */
export const RegenProvider = ({ children }: PropsWithChildren) => {
  const { selectedIndividual } = useIndividuals()
  const regen = useRegenState(selectedIndividual)
  const offer = useRegenOffer(selectedIndividual, regen)

  return (
    <RegenContext.Provider value={{ ...regen, offer }}>{children}</RegenContext.Provider>
  )
}

export function useRegen() {
  const context = useContext(RegenContext)

  if (context === undefined) {
    throw new Error('useRegen must be used within a RegenProvider')
  }

  return context
}
